// QuickCreateEditField — the "Add More" / "Edit" affordance inside a select
// dropdown. Fully admin-config driven (FormGroupField.quickCreate / quickEdit,
// see the Go struct). When a select carries quickCreate, its dropdown grows an
// "Add More" button that opens the target module's Add form in a modal; on a
// successful create the host field refetches its options and selects the new
// record. quickEdit adds an "Edit" button that opens the target module's Edit
// form for a resolved record id (this field's own value, or a sibling field via
// quickEditIdFrom — e.g. "edit the SELECTED CLIENT to add a contact" from the
// job's Contact Person field). No module or field name is hardcoded here.
//
// A field may also carry `quickCreateNotice` (see ./quickCreateNotice.js): a
// popup shown the instant a record is created HERE, warning that a brand-new
// record is not yet configured. Because only a real quick-create reaches that
// code path, the notice cannot fire for an existing record that was merely
// selected.
//
// AddFormV1/EditFormV1 are pulled in with React.lazy so this module can be
// imported by those same files without a static circular dependency.
import React, { Suspense, lazy, useMemo, useState } from 'react';
import { quickCreateLabel, quickEditLabel, quickModalTitle, humanizeModule } from './quickActionLabels';
import { Modal, Space, Spin, Button } from 'antd';
import { PlusOutlined, EditOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import AppButton from '../AppButton';
import { normalizeQuickCreateNotice, resolveQuickCreateNoticeDetails } from './quickCreateNotice';

const AddFormV1 = lazy(() => import('../AddFormV1'));
const EditFormV1 = lazy(() => import('../EditFormV1'));

const empty = (v) => v === undefined || v === null || v === '';
const pathOf = (key) => (typeof key === 'string' && key.includes('.') ? key.split('.') : [key]);

// Resolve the record id a quick-edit should open. Priority:
//   1. field.quickEditIdFrom → a sibling field's value (row-scoped inside an
//      addRow row, else top-level; falls back to scopeValues, e.g. clientId).
//   2. this field's own selected value (labeled selects carry {value}).
function resolveEditId(field, form, name, scopeValues, ownValue) {
    const idFrom = field.quickEditIdFrom;
    if (idFrom) {
        const insideRow = Array.isArray(name) && name.length >= 3 && typeof name[1] === 'number';
        const abs = insideRow ? [...name.slice(0, 2), ...pathOf(idFrom)] : pathOf(idFrom);
        let v = form?.getFieldValue?.(abs);
        if (empty(v)) v = scopeValues?.[idFrom];
        return empty(v) ? null : v;
    }
    if (ownValue && typeof ownValue === 'object') return ownValue.value ?? null;
    return empty(ownValue) ? null : ownValue;
}

/**
 * useQuickField — returns { footer, modal } for a select FieldControl.
 *   footer: JSX to append inside the Select's dropdownRender (the action buttons)
 *   modal:  JSX to render alongside the Select (the create/edit Modal)
 * onDone(kind, recordId) fires after a successful create/edit so the caller can
 * refetch options (and, for create, select the new record).
 */
export function useQuickField({ field, form, name, moduleName, scopeValues, ownValue, onDone }) {
    const [modal, setModal] = useState(null); // { mode: 'create'|'edit', module, recordId }
    // The post-create notice (see handleCreated below). Held here — above the
    // early return — because hooks may not be called conditionally.
    const [notice, setNotice] = useState(null); // { title, message, recordLabel, ... , recordName, creatorName }

    const quickCreate = Boolean(field?.quickCreate);
    const quickEdit = Boolean(field?.quickEdit);
    const createModule = field?.quickCreateModule || field?.lookupCollection || '';
    const editModule = field?.quickEditModule || field?.lookupCollection || '';

    const editId = useMemo(
        () => (quickEdit ? resolveEditId(field, form, name, scopeValues, ownValue) : null),
        // ownValue / sibling changes should re-resolve
        [quickEdit, field, form, name, scopeValues, ownValue],
    );

    if (!quickCreate && !quickEdit) return { footer: null, modal: null };

    const close = () => setModal(null);
    // quickCreateOnClick (opt-in): call a plain local function instead of
    // opening the generic module's Add form or navigating — for a create
    // flow the page already has its own custom modal/logic for. Checked
    // before quickCreateRoute; existing fields without either stay on
    // today's default modal behavior.
    const openCreate = () => {
        if (field?.quickCreateOnClick) {
            field.quickCreateOnClick();
            // This bypass opens the caller's own external modal, not the built-in
            // in-dropdown create flow — so unlike that flow, there's no reason to
            // keep the Select's dropdown open underneath it. Close it immediately
            // instead of letting it linger until the new modal's mask steals focus.
            document.activeElement?.blur?.();
            return;
        }
        if (field?.quickCreateRoute) {
            window.open(field.quickCreateRoute, '_blank', 'noopener,noreferrer');
            return;
        }
        createModule && setModal({ mode: 'create', module: createModule });
    };
    const openEdit = () => editModule && editId && setModal({ mode: 'edit', module: editModule, recordId: String(editId) });

    // ── The "brand-new record" notice ────────────────────────────────────────
    // WHY it hooks the quick-create success path and nothing else:
    // reaching this callback is only possible by having just SAVED a record
    // through the in-dropdown Add form. Selecting an existing option never
    // travels through here at all. That makes "new records only" a STRUCTURAL
    // guarantee of where the code sits, not a runtime test we have to keep
    // true. A heuristic ("does this id look new?", "was it absent from the
    // options list?") would be a second source of truth that drifts the first
    // time options are cached, paginated or server-filtered — so we
    // deliberately do NOT add one.
    //
    // WHY it fires now instead of after the outer form is submitted: the stated
    // purpose is to stop the recruiter proceeding as though a brand-new client
    // were fully configured. A warning shown after they finished the submission
    // is a receipt, not a guard. It must land while the decision to continue is
    // still ahead of them.
    const handleCreated = (newId) => {
        const config = normalizeQuickCreateNotice(field);
        if (!config) return;
        // Open immediately with whatever we know (nothing yet). The two lookups
        // below only ever ENRICH this; they can never delay or cancel it, which
        // is what "fail open" means here.
        setNotice({ ...config, recordName: '', creatorName: '' });
        resolveQuickCreateNoticeDetails({
            module: createModule,
            recordId: newId,
            notice: config,
            displayField: field?.displayField || '',
        })
            .then(({ recordName, creatorName }) => {
                setNotice((prev) => (prev ? { ...prev, recordName, creatorName } : prev));
            })
            .catch(() => { /* already open; the message is the important part */ });
    };

    const footer = (
        <div
            className="v1-quick-actions"
            role="presentation"
            onMouseDown={(e) => e.preventDefault()} // keep the select open while clicking
            style={{ display: 'flex', gap: 8, padding: '6px 8px', borderTop: '1px solid rgba(0,0,0,0.06)' }}
        >
            <Space size={8}>
                {quickCreate && (
                    <Button type="link" size="small" icon={<PlusOutlined />} onClick={openCreate} style={{ paddingLeft: 0 }}>
                        {quickCreateLabel(field, createModule)}
                    </Button>
                )}
                {quickEdit && (
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={openEdit}
                        disabled={!editId}
                        title={!editId ? `Choose a ${humanizeModule(editModule) || 'record'} above first, then edit it here` : undefined}
                    >
                        {quickEditLabel(field, editModule)}
                    </Button>
                )}
            </Space>
        </div>
    );

    const modalNode = modal ? (
        <Modal
            open
            title={quickModalTitle(modal.mode, field, modal.mode === 'create' ? createModule : editModule)}
            width="min(1080px, 96vw)"
            footer={null}
            destroyOnClose
            maskClosable={false}
            onCancel={close}
            styles={{ body: { maxHeight: '78vh', overflowY: 'auto' } }}
        >
            <Suspense fallback={<div style={{ padding: 48, textAlign: 'center' }}><Spin /></div>}>
                {modal.mode === 'create' ? (
                    <AddFormV1
                        moduleName={modal.module}
                        embedded
                        breadcrumbItems={[]}
                        onCancel={close}
                        onSuccess={(newId) => { close(); handleCreated(newId); onDone?.('create', newId); }}
                    />
                ) : (
                    <EditFormV1
                        moduleName={modal.module}
                        recordId={modal.recordId}
                        embedded
                        breadcrumbItems={[]}
                        onCancel={close}
                        onSuccess={(rid) => { close(); onDone?.('edit', rid); }}
                    />
                )}
            </Suspense>
        </Modal>
    ) : null;

    // Informational/warning notice. Rendered as a sibling of the create modal
    // (never nested inside it) so it survives that modal being destroyed on
    // close — the create form unmounts the moment it succeeds.
    const noticeNode = notice ? (
        <Modal
            open
            title={(
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <ExclamationCircleFilled style={{ color: '#faad14' }} />
                    {notice.title}
                </span>
            )}
            width="min(520px, 94vw)"
            maskClosable={false}
            onCancel={() => setNotice(null)}
            footer={(
                <AppButton variant="primary" type="primary" onClick={() => setNotice(null)}>
                    OK
                </AppButton>
            )}
        >
            <p style={{ marginTop: 0, marginBottom: 16 }}>{notice.message}</p>
            {/* Traceability pair. Each line is dropped when its lookup produced
                nothing — an empty "Created by:" is noise, not information (the
                same rule detail/userNames.js applies to unresolvable actors). */}
            {(notice.recordName || notice.creatorName) && (
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr',
                        gap: '6px 12px',
                        padding: '10px 12px',
                        borderRadius: 6,
                        background: 'rgba(0,0,0,0.03)',
                    }}
                >
                    {notice.recordName && (
                        <>
                            <span style={{ color: 'rgba(0,0,0,0.55)' }}>{notice.recordLabel}</span>
                            <strong>{notice.recordName}</strong>
                        </>
                    )}
                    {notice.creatorName && (
                        <>
                            <span style={{ color: 'rgba(0,0,0,0.55)' }}>{notice.creatorLabel}</span>
                            <strong>{notice.creatorName}</strong>
                        </>
                    )}
                </div>
            )}
        </Modal>
    ) : null;

    return {
        footer,
        modal: (modalNode || noticeNode) ? (<>{modalNode}{noticeNode}</>) : null,
    };
}
